import { useEffect, useMemo, useRef, useState } from 'react';
import type { CapturedPiece, HandicapConfig, PlayerRef, ResultReason, Side } from '@xiangqi/protocol';
import { RESUME_WINDOW_MS } from '@xiangqi/protocol';
import type { HandicapConfigJson, PieceKindId, RulesetId } from '@xiangqi/game-core';
import { api } from '../api.ts';
import { useMatch } from '../game/useMatch.ts';
import { isRoyalPiece } from '../game/premove.ts';
import { BLACK_CHARS, Board, COVERED_ASSET, PIECE_ASSETS, RED_CHARS, ROYAL_PIECE_ASSETS } from './Board.tsx';
import { Clock } from './Clock.tsx';
import { ChatDrawer } from './ChatDrawer.tsx';
import { SettingsModal } from './SettingsModal.tsx';
import { GameReviewModal } from './GameReviewModal.tsx';
import { buildGameReviewFromReplay, type GameReviewReport } from '../game/game-review.ts';

const REASON_TEXT: Record<ResultReason, string> = {
  CHECKMATE: 'Chiếu bí',
  STALEMATE: 'Hết nước đi',
  PERPETUAL_CHECK: 'Chiếu dai — phạm luật',
  PERPETUAL_CHASE: 'Đuổi dai — phạm luật',
  REPETITION_DRAW: 'Hòa do lặp vị trí',
  SIXTY_MOVE_DRAW: 'Hòa 60 nước không ăn quân',
  RESIGN: 'Đầu hàng',
  TIMEOUT: 'Hết giờ',
  DRAW_AGREED: 'Hòa thỏa thuận',
  ABANDON: 'Đối thủ rời ván quá lâu',
};

interface MatchScreenProps {
  matchId: string;
  navigate(path: string): void;
}

export function MatchScreen({ matchId, navigate }: MatchScreenProps) {
  const { view, actions, core } = useMatch(matchId);
  const [confirmResign, setConfirmResign] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [restarting, setRestarting] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [showStreamerPanel, setShowStreamerPanel] = useState(false);
  const [streamerLayout, setStreamerLayout] = useState<'standard' | 'widescreen' | 'vertical' | 'clean'>(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 900) return 'widescreen';
    return 'standard';
  });
  const [chromaKey, setChromaKey] = useState(false);
  const [hideSensitive, setHideSensitive] = useState(false);
  const [showCheckBanner, setShowCheckBanner] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'f' || e.key === 'F') {
        setFlipped((prev) => !prev);
      } else if (e.key === 'b' || e.key === 'B') {
        setStreamerLayout((prev) => (prev === 'clean' ? 'widescreen' : 'clean'));
      } else if (e.key === 'g' || e.key === 'G') {
        setChromaKey((prev) => !prev);
      } else if (e.key === 's' || e.key === 'S') {
        setShowStreamerPanel((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);
  const [showResult, setShowResult] = useState(true);
  const [reviewReport, setReviewReport] = useState<GameReviewReport | null>(null);
  const [loadingReview, setLoadingReview] = useState(false);
  const prevCheckPly = useRef<number>(-1);

  const handleOpenReview = async () => {
    try {
      setLoadingReview(true);
      try {
        const reviewRes = await api.review(matchId);
        if (reviewRes?.report) {
          setReviewReport(reviewRes.report);
          return;
        }
      } catch {
        // Fallback to client replay evaluation
      }
      const res = await api.replay(matchId);
      if (res?.replay) {
        const report = buildGameReviewFromReplay(res.replay, core);
        setReviewReport(report);
      }
    } catch {
      setToast('Không thể tải dữ liệu phân tích ván đấu');
    } finally {
      setLoadingReview(false);
    }
  };

  const inCheck = useMemo(() => {
    if (!core || !view.state || view.status !== 'running') return false;
    try {
      return core.legalMoves(view.state).in_check;
    } catch {
      return false;
    }
  }, [core, view.state, view.status]);

  useEffect(() => {
    if (inCheck && view.ply !== prevCheckPly.current) {
      prevCheckPly.current = view.ply;
      setShowCheckBanner(true);
      const timer = setTimeout(() => setShowCheckBanner(false), 1400);
      return () => clearTimeout(timer);
    }
    if (!inCheck) {
      setShowCheckBanner(false);
    }
  }, [inCheck, view.ply]);

  useEffect(() => {
    if (view.result) setShowResult(true);
  }, [view.result]);

  useEffect(() => {
    setConfirmResign(false);
  }, [view.result]);

  useEffect(() => {
    if (!view.rejection) return undefined;
    setToast(view.rejection.text);
    const t = setTimeout(() => setToast(null), 4_000);
    return () => clearTimeout(t);
  }, [view.rejection]);

  useEffect(() => {
    if (view.rematchStartedNextMatchId) {
      navigate(`/m/${view.rematchStartedNextMatchId}`);
    }
  }, [view.rematchStartedNextMatchId, navigate]);

  if (!view.loaded || !view.state || !view.sides) {
    return (
      <main className="page">
        <p className="waiting">Đang tải ván đấu…</p>
      </main>
    );
  }

  const spectator = view.yourSide === null;
  const you: Side = view.yourSide ?? 'red';
  const opp: Side = you === 'red' ? 'black' : 'red';
  const running = view.status === 'running';
  const yourTurn = !spectator && running && view.state.turn === you && !view.pendingMove;

  const isRoyalRuleset =
    view.state.ruleset === 'blind_si_chua' || view.state.ruleset === 'blind_tuong_vuong';

  const handicapTotalMoves =
    view.state.ruleset === 'blind'
      ? 0
      : (view.handicap?.current_moves ??
        (view.handicap?.type === 'asym_royal' && isRoyalRuleset
          ? (view.state.ruleset === 'blind_tuong_vuong' ? 6 : 7)
          : view.handicap?.moves_count));

  const isHandicapOpening =
    running &&
    view.handicap &&
    view.state.ruleset !== 'blind' &&
    ((view.handicap.type === 'moves' && view.state.ruleset === 'standard') ||
      (view.handicap.type === 'asym_royal' && isRoyalRuleset)) &&
    view.ply < (handicapTotalMoves ?? 0);

  const isLocal = matchId.startsWith('local-');

  const handlePlayAgain = () => {
    if (isLocal) {
      setRestarting(true);
      actions.offerRematch();
      setTimeout(() => {
        setRestarting(false);
        setShowResult(false);
      }, 300);
      return;
    }
    actions.offerRematch();
    setToast('Đã gửi lời mời tiếp tục chơi tới đối thủ!');
  };

  return (
    <main className={`page match layout-${streamerLayout} ${chromaKey ? 'chroma-key-mode' : ''} ${hideSensitive ? 'hide-sensitive' : ''}`}>
      <div className="match-top-bar">
        <button className="match-bar-btn" onClick={() => navigate('/')} title="Về sảnh">
          ← Sảnh
        </button>
        <div className="spectator-counter">
          👁️ {view.spectatorCount} người xem
        </div>
        <div className="match-top-actions">
          <button
            className={`match-bar-btn streamer-btn ${showStreamerPanel || streamerLayout !== 'standard' || chromaKey ? 'active' : ''}`}
            onClick={() => setShowStreamerPanel(!showStreamerPanel)}
            title="Công cụ Livestream (OBS / TikTok) [Phím S]"
          >
            🎥 Live
          </button>
          <button
            className={`match-bar-btn flip-btn ${flipped ? 'active' : ''}`}
            onClick={() => setFlipped(!flipped)}
            title="Đảo bàn cờ [Phím F]"
          >
            🔄
          </button>
          <button
            className="match-bar-btn chat-btn"
            onClick={() => setShowChat(true)}
            title="Trò chuyện"
          >
            💬 {view.chatMessages.length > 0 && <span className="chat-badge">{view.chatMessages.length}</span>}
          </button>
          <button
            className="match-bar-btn settings-btn"
            onClick={() => setShowSettings(true)}
            title="Cài đặt"
          >
            ⚙️
          </button>
        </div>
      </div>

      {showStreamerPanel && (
        <div className="streamer-control-panel">
          <div className="streamer-panel-header">
            <h4>🎥 Công cụ Livestream (macOS & OBS)</h4>
            <button className="close-btn" onClick={() => setShowStreamerPanel(false)}>✕</button>
          </div>
          <div className="streamer-panel-section">
            <span className="section-label">Bố cục hiển thị:</span>
            <div className="streamer-layout-buttons">
              <button
                className={`streamer-btn-opt ${streamerLayout === 'widescreen' ? 'active' : ''}`}
                onClick={() => setStreamerLayout('widescreen')}
                title="Bàn cờ to bên trái, bảng điều khiển bên phải cho YouTube/Facebook"
              >
                🖥️ 16:9 Ngang
              </button>
              <button
                className={`streamer-btn-opt ${streamerLayout === 'vertical' ? 'active' : ''}`}
                onClick={() => setStreamerLayout('vertical')}
                title="Khung dọc tối ưu TikTok Live Studio"
              >
                📱 9:16 Dọc
              </button>
              <button
                className={`streamer-btn-opt ${streamerLayout === 'clean' ? 'active' : ''}`}
                onClick={() => setStreamerLayout('clean')}
                title="Ẩn toàn bộ nút bấm, chỉ để bàn cờ và đồng hồ siêu nét [Phím B]"
              >
                🎯 Clean Board
              </button>
            </div>
          </div>

          <div className="streamer-panel-section toggles">
            <label className="streamer-toggle-item">
              <input
                type="checkbox"
                checked={chromaKey}
                onChange={(e) => setChromaKey(e.target.checked)}
              />
              <span>🟢 Phông Xanh Chroma Key (cho OBS lọc nền) [Phím G]</span>
            </label>
            <label className="streamer-toggle-item">
              <input
                type="checkbox"
                checked={hideSensitive}
                onChange={(e) => setHideSensitive(e.target.checked)}
              />
              <span>🛡️ Chống Soi Bài (Ẩn tên nhạy cảm & đối thủ)</span>
            </label>
          </div>

          <div className="streamer-tips">
            <div className="tip-title">💡 Mẹo Streamer:</div>
            <ul>
              <li><strong>Chuột phải kéo:</strong> Vẽ mũi tên chiến thuật giảng cờ</li>
              <li><strong>Chuột phải click:</strong> Đánh dấu ô cờ cần chú ý</li>
              <li><strong>Phím C:</strong> Xóa toàn bộ mũi tên</li>
              <li><strong>Phím F:</strong> Đảo chiều bàn cờ</li>
              <li><strong>Phím B:</strong> Bật/tắt chế độ Clean Board</li>
            </ul>
          </div>
        </div>
      )}

      {view.conn === 'reconnecting' && (
        <div className="banner reconnect" data-testid="reconnect-banner">
          Mất kết nối — đang nối lại… (đồng hồ vẫn chạy)
        </div>
      )}
      {view.conn === 'closed' && (
        <div className="banner error">Không nối lại được. Tải lại trang để tiếp tục.</div>
      )}

      {view.rematchOfferBy && view.rematchOfferBy !== view.yourSide && (
        <div className="rematch-offer-banner">
          <div className="rematch-offer-text">
            🤝 <strong>Đối thủ muốn tiếp tục chơi với bạn!</strong>
          </div>
          <div className="rematch-offer-actions">
            <button
              className="rematch-btn accept"
              onClick={() => actions.respondRematch(true)}
            >
              ✓ Đồng ý
            </button>
            <button
              className="rematch-btn decline"
              onClick={() => actions.respondRematch(false)}
            >
              ✕ Từ chối
            </button>
          </div>
        </div>
      )}

      <PlayerBar player={view.sides[opp]} side={opp} view={view} running={running} hideSensitive={hideSensitive} />
      <CapturedTray
        captured={view.capturedPieces.filter((p) => p.captured_by === opp)}
        side={opp}
        label={`Quân ${opp === 'red' ? 'Đỏ' : 'Đen'} đã ăn`}
        ruleset={view.state.ruleset}
        handicap={view.state.handicap ?? view.handicap}
        isYou={false}
        isGameOver={view.result !== null}
      />

      {spectator && <div className="banner">👁 Chế độ xem — bạn không tham gia ván này</div>}
      <div className="board-wrapper" style={{ position: 'relative' }}>
        {showCheckBanner && (
          <div className="check-alert-banner">
            <div className="check-alert-box">
              <div className="check-kanji">將</div>
              <div className="check-text">CHIẾU TƯỚNG!</div>
            </div>
          </div>
        )}
        <Board
          state={view.state}
          core={core}
          yourSide={view.yourSide}
          flipped={flipped}
          interactive={!spectator && running && !view.pendingMove && view.result === null}
          lastMove={view.lastMove}
          pendingMove={view.pendingMove}
          premove={view.premove}
          handicap={view.state.handicap ?? view.handicap}
          inCheck={inCheck}
          onMove={actions.move}
          onCancelPremove={actions.cancelPremove}
        />
      </div>

      <CapturedTray
        captured={view.capturedPieces.filter((p) => p.captured_by === you)}
        side={you}
        label={`Quân ${you === 'red' ? 'Đỏ' : 'Đen'} đã ăn`}
        ruleset={view.state.ruleset}
        handicap={view.state.handicap ?? view.handicap}
        isYou={!spectator}
        isGameOver={view.result !== null}
      />
      <PlayerBar player={view.sides[you]} side={you} view={view} running={running} you={!spectator} hideSensitive={hideSensitive} />

      <div className="statusline" data-testid="statusline">
        {view.status === 'waiting' && 'Chờ cả hai người chơi kết nối…'}
        {running &&
          (isHandicapOpening
            ? `Nước chấp ${view.ply + 1}/${handicapTotalMoves} (${view.state.turn === 'red' ? 'Đỏ' : 'Đen'} đi)${view.state.ruleset.startsWith('blind') && (handicapTotalMoves ?? 0) > 1 ? ' · Mỗi quân 1 lần (mở đồng loạt)' : ''}`
            : spectator
              ? `Chế độ xem · lượt ${view.state.turn === 'red' ? 'Đỏ' : 'Đen'}`
              : yourTurn
                ? 'Đến lượt bạn'
                : view.pendingMove
                  ? 'Đang gửi nước đi…'
                  : 'Chờ đối thủ đi')}
        {view.premove && <span className="premove-status"> · Nước chờ đã đặt <button type="button" onClick={actions.cancelPremove}>Hủy</button></span>}
        {toast && <span className="error"> · {toast}</span>}
      </div>

      {running && !spectator && (
        <div className="controls">
          {confirmResign ? (
            <>
              <span>Chắc chắn đầu hàng?</span>
              <button className="danger" onClick={() => actions.resign()} data-testid="resign-confirm">
                Đầu hàng
              </button>
              <button onClick={() => setConfirmResign(false)}>Không</button>
            </>
          ) : (
            <>
              <button onClick={() => setConfirmResign(true)} data-testid="resign">
                Đầu hàng
              </button>
              <button onClick={() => actions.offerDraw()} data-testid="offer-draw">
                Cầu hòa
              </button>
            </>
          )}
        </div>
      )}

      {view.drawOfferBy && view.drawOfferBy !== you && running && !spectator && (
        <div className="banner draw" data-testid="draw-offer">
          Đối thủ cầu hòa —
          <button className="primary" onClick={() => actions.respondDraw(true)} data-testid="draw-accept">
            Đồng ý
          </button>
          <button onClick={() => actions.respondDraw(false)}>Từ chối</button>
        </div>
      )}
      {view.drawOfferBy === you && running && <div className="banner">Đã gửi lời cầu hòa…</div>}

      {view.result && showResult && (() => {
        const isWin = !spectator && view.result.winner === you;
        const isLoss = !spectator && view.result.winner === opp;
        const isDraw = view.result.winner === null;
        const variantClass = isWin ? 'victory' : isLoss ? 'defeat' : 'draw';
        const emblem = isWin ? '🏆' : isLoss ? '⚔️' : '🤝';
        const title = isDraw
          ? 'HÒA CUỘC'
          : spectator
            ? `${view.result.winner === 'red' ? 'ĐỎ' : 'ĐEN'} THẮNG`
            : isWin
              ? 'CHIẾN THẮNG!'
              : 'THẤT BẠI';
        const quote = isWin
          ? 'Kỳ nghệ tinh thông, mưu lược phi phàm!'
          : isLoss
            ? 'Thắng bại là chuyện thường tình của binh gia. Tái chiến phục thù!'
            : 'Kỳ phùng địch thủ, tài trí ngang cơ!';

        return (
          <div className="modal-backdrop game-over-backdrop" data-testid="result-backdrop">
            <div className={`game-over-modal ${variantClass}`} data-testid="result">
              <div className="game-over-emblem">{emblem}</div>
              <h2 className="game-over-title">{title}</h2>
              <div className="game-over-reason">
                {REASON_TEXT[view.result.reason] ?? view.result.reason}
              </div>
              {view.state?.ruleset !== 'standard' && (
                <div className="game-over-reveal-note" data-testid="final-reveal-note">
                  Đã mở toàn bộ quân úp — danh tính quân còn lại và quân đã bị ăn đều được hiển thị.
                </div>
              )}
              <div className="game-over-quote">{quote}</div>
              <div className="game-over-actions">
                <button
                  className="game-over-btn primary-action"
                  onClick={() => handlePlayAgain()}
                  disabled={restarting || view.rematchOfferBy === view.yourSide}
                  data-testid="play-again"
                >
                  {restarting
                    ? 'Đang khởi tạo…'
                    : view.rematchOfferBy === view.yourSide
                    ? '⏳ Đang chờ đối thủ đồng ý…'
                    : '🔄 Tiếp tục chơi với đối thủ'}
                </button>
                <button
                  className="game-over-btn secondary-action"
                  onClick={() => navigate(`/replay/${matchId}`)}
                >
                  🎬 Xem lại ván cờ
                </button>
                <button
                  className="game-over-btn review-action"
                  onClick={() => void handleOpenReview()}
                  disabled={loadingReview}
                  style={{
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                    borderColor: '#60a5fa',
                    color: '#fff',
                    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                  }}
                >
                  {loadingReview ? 'Đang phân tích ván cờ…' : '📊 Phân tích ván đấu (AI Review)'}
                </button>
                {view.state?.ruleset !== 'standard' && (
                  <button
                    className="game-over-btn tertiary-action"
                    onClick={() => setShowResult(false)}
                    data-testid="show-final-board"
                  >
                    👁 Xem bàn cờ đã mở
                  </button>
                )}
                <button
                  className="game-over-btn tertiary-action"
                  onClick={() => navigate('/')}
                >
                  🏠 Về trang chủ
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <ChatDrawer
        open={showChat}
        onClose={() => setShowChat(false)}
        messages={view.chatMessages}
        onSendMessage={actions.sendChat}
        yourSide={view.yourSide}
      />

      <SettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        onFlipBoard={() => setFlipped(!flipped)}
        flipped={flipped}
        canAct={running && !spectator}
        onResign={() => actions.resign()}
        onOfferDraw={() => actions.offerDraw()}
      />

      {reviewReport && (
        <GameReviewModal
          report={reviewReport}
          onClose={() => setReviewReport(null)}
          onViewReplay={(targetStep) => {
            setReviewReport(null);
            const stepQuery = typeof targetStep === 'number' ? `&step=${targetStep}` : '';
            navigate(`/replay/${matchId}?review=1${stepQuery}`);
          }}
        />
      )}
    </main>
  );
}

function PlayerBar({
  player,
  side,
  view,
  running,
  you = false,
  hideSensitive = false,
}: {
  player: PlayerRef;
  side: Side;
  view: ReturnType<typeof useMatch>['view'];
  running: boolean;
  you?: boolean;
  hideSensitive?: boolean;
}) {
  const disconnectedOpponent = running && !you && !player.connected;
  const nameToDisplay = hideSensitive && !you ? 'Đối thủ ẩn danh' : player.display_name;
  return (
    <div className={`playerbar ${side}`} data-testid={`player-${side}`}>
      <span className={`dot ${player.connected ? 'on' : 'off'}`} aria-hidden />
      <span className="name">
        {nameToDisplay}
        {you ? ' (bạn)' : ''} · {side === 'red' ? 'Đỏ' : 'Đen'}
        {view.handicap?.time && (
          <span className="hint" style={{ marginLeft: '6px', fontSize: '12px' }}>
            {view.handicap.giver_side === side ? '⚡Chấp giờ' : '⏱️Được chấp'}
          </span>
        )}
      </span>
      {disconnectedOpponent && (
        <span className="hint">
          mất kết nối — tự thua sau {Math.round(RESUME_WINDOW_MS / 1000)}s
        </span>
      )}
      {view.clocks && (
        <Clock clocks={view.clocks} side={side} serverOffset={view.serverOffset} running={running} />
      )}
    </div>
  );
}

function CapturedTray({
  captured,
  side,
  label,
  ruleset,
  handicap,
  isYou = false,
  isGameOver = false,
}: {
  captured: CapturedPiece[];
  side: Side;
  label: string;
  ruleset?: string;
  handicap?: HandicapConfig | HandicapConfigJson | null;
  isYou?: boolean;
  isGameOver?: boolean;
}) {
  if (captured.length === 0) return null;
  const isRoyalRuleset =
    ruleset === 'blind_si_chua' || ruleset === 'blind_tuong_vuong' || ruleset === 'blind_royal';

  return (
    <div className={`captured-tray ${side}`} data-testid={`captured-${side}`}>
      <span className="tray-label">{label}:</span>
      <div className="tray-pieces">
        {captured.map((c, idx) => {
          const isCoveredUnknown = !c.kind || (!isYou && c.was_covered && !isGameOver);
          const chars = c.side === 'red' ? RED_CHARS : BLACK_CHARS;
          const char = isCoveredUnknown ? '？' : c.kind ? chars[c.kind as PieceKindId] : '？';
          const isRoyal =
            isRoyalRuleset &&
            !c.was_covered &&
            isRoyalPiece(
              ruleset as RulesetId,
              { side: c.side, kind: c.kind as PieceKindId, covered: c.was_covered },
              handicap,
            );

          const imgSrc = isCoveredUnknown
            ? COVERED_ASSET
            : isRoyal && c.kind && ROYAL_PIECE_ASSETS[c.side][c.kind as PieceKindId]
              ? ROYAL_PIECE_ASSETS[c.side][c.kind as PieceKindId]!
              : c.kind
                ? PIECE_ASSETS[c.side][c.kind as PieceKindId]
                : COVERED_ASSET;

          const royalTitle =
            c.kind === 'advisor' ? 'Sĩ Chúa' : c.kind === 'elephant' ? 'Tượng Vương' : c.kind;

          return (
            <span
              key={idx}
              className={`captured-piece ${c.side} ${c.was_covered ? 'was-covered' : ''} ${isCoveredUnknown ? 'covered' : ''} ${isRoyal ? 'royal' : ''}`}
              title={
                isCoveredUnknown
                  ? `Quân úp bị ăn của bên ${c.side === 'red' ? 'Đỏ' : 'Đen'}`
                  : `${c.side === 'red' ? 'Đỏ' : 'Đen'} ${isRoyal ? royalTitle : c.kind}${c.was_covered ? ' (ăn lúc còn úp)' : ''}`
              }
            >
              <img src={imgSrc} alt={char} className="captured-piece-img" />
            </span>
          );
        })}
      </div>
    </div>
  );
}
